import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; 
import { FaDownload } from 'react-icons/fa';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaCloudUploadAlt
} from 'react-icons/fa';
import { useScreenPermissions } from '../components/useScreenPermissions'; 
import { useNavigate } from 'react-router-dom';
import MaterialIssueFormModal from './MaterialIssueFormModal';
import MaterialIssueViewModal from './MaterialIssueViewModal';
import PDFDesigner from '../components/pdfDesigner';
import axios from "axios";

// Add this component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = '/api'; 

// --- STYLES & THEME ---
const COLORS = {
  CLOUD: '#F5EFE6',
  BABY: '#F0ECE2',
  BABY1: '#B88A5A',
  DIVIDER: '#E6D9CC',
  BORDER: '#E6D9CC',
  TEXT: '#211f1eff',
  MUTED: '#6E584A',
  BADGE_BG: '#F6EFE4',
  SUCCESS_BG: '#EAF3EE',
  WARNING_BG: '#FBF2E8',
  PRIMARY_HOVER: '#A27246'
};

// --- ACTION MODAL ---
const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;
  let icon, title, bgColor, textColor;

  if (status === 'loading') {
    bgColor = 'bg-blue-100'; textColor = 'text-blue-600'; title = 'Processing...';
    icon = <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>;
  } else if (status === 'success') {
    bgColor = 'bg-green-100'; textColor = 'text-green-600'; title = 'Done';
    icon = <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
  } else {
    bgColor = 'bg-red-100'; textColor = 'text-red-600'; title = 'Error';
    icon = <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>;
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[71]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>{icon}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        {status === 'error' && <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Close</button>}
      </div>
    </div>
  );
};

// --- CONFIRMATION DIALOG ---
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;
  const iconBgColor = isDanger ? 'bg-red-100' : 'bg-teal-100';
  const confirmButtonClass = isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-700 hover:bg-teal-800';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onCancel}></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[61]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
          {isDanger ? <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg> : <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium rounded text-white ${confirmButtonClass}`}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const MaterialIssueItems = () => {
  // Theme Injection
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER, '--sidebar-bg': '#ffffff', '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1, '--accent-hover': COLORS.PRIMARY_HOVER, '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT, '--text-muted': COLORS.MUTED, '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3', '--search-icon': COLORS.MUTED, '--hover-gray': COLORS.BADGE_BG
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
  }, []);

  // Scoped CSS
  useEffect(() => {
    const styleId = 'materialissue-overrides';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .materialissue-root .rounded-md, .materialissue-root .rounded-lg { border-radius: 4px !important; }
      .materialissue-root input, .materialissue-root select { background: #ffffff !important; border: 1px solid ${COLORS.BORDER} !important; color: ${COLORS.TEXT} !important; }
      .materialissue-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .materialissue-root table tbody tr { position: relative; }
      .materialissue-root table tbody tr::after { content: ""; position: absolute; left: 0; right: 0; height: 1px; background: ${COLORS.DIVIDER}; bottom: 0; pointer-events: none; }
      .materialissue-root .status-badge { border-radius: 9999px !important; display: inline-flex !important; align-items: center; padding: 0.25rem 0.75rem; font-size: 12px; font-weight: 600; }
    `;
    document.head.appendChild(style);
  }, []);

  // State
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  // Add this state near your other useState declarations
const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);

  // --- NEW ADDED STATE FOR APPROVAL ---
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    issueHeaderID: null, 
    currentLevel: null,
    remarks: ''
  });

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    id: null,
    issueNo: '',
    action: ''
  });

    // --- TOAST STATE & HELPER ---
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // Action/Confirm States
  const [actionModal, setActionModal] = useState({ isOpen: false, status: 'loading', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, isDanger: false });

  const navigate = useNavigate();

  // Sorting & Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Fetch Data
  const fetchMaterialIssues = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-issue-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setIssues(Array.isArray(data) ? data : []);
    } catch (error) {
      // console.error('Error fetching Material Issues:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMaterialIssues(); }, []);

  // Handlers
  const handleCreate = () => {
    setSelectedIssue(null);
    setShowFormModal(true);
  };

  const handleEdit = async (issue) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-issue/${issue.IssueHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedIssue(fullData);
      setShowFormModal(true);
    } catch (error) {
      // console.error('Error fetching details:', error);
      setActionModal({ isOpen: true, status: 'error', message: 'Could not load record details' });
    }
  };

  const handleView = async (issue) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-issue/${issue.IssueHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedIssue(fullData);
      setShowViewModal(true);
    } catch (error) {
      // console.error('Error fetching view details:', error);
      setActionModal({ isOpen: true, status: 'error', message: 'Could not load record details' });
    }
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Material Issue',
      message: 'Are you sure you want to delete this Material Issue?',
      isDanger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/material-issue/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();

        if (!response.ok) {
          showToast(
            result.message || 'Delete not allowed',
            'error',
            'Action Not Allowed'
          );
          return;
        }
          // if (!response.ok) throw new Error('Failed to delete');
          fetchMaterialIssues();
          showToast('Deleted successfully', 'success', 'Done');
          // setActionModal({ isOpen: true, status: 'success', message: 'Deleted successfully' });
          // setTimeout(() => setActionModal({ isOpen: false }), 2000);
        } catch (error) {
          // console.error('Error deleting:', error);
          // setActionModal({ isOpen: true, status: 'error', message: 'Error deleting record' });
          showToast('Error deleting record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  const handleSendToBc = (issue) => {
    if (issue.SendToBC) {
      setActionModal({ isOpen: true, status: 'error', message: 'This Issue has already been sent to BC.' });
      return;
    }
    setConfirmModal({
      isOpen: true, title: 'Send to BC', message: `Send Issue ${issue.IssueHeaderNo} to BC?`, isDanger: false,
      onConfirm: async () => {
        setActionModal({ isOpen: true, status: 'loading', message: 'Sending to Business Central...' });
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/mapping/material-issue/send-to-bc`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ issueHeaderID: issue.IssueHeaderID })
          });
          const data = await response.json(); // 👈 ADD THIS

          if (!response.ok) {
            throw new Error(
              data?.message || 
              data?.error?.message || 
              'Failed to send to BC'
            );
          }
          fetchMaterialIssues();
          setActionModal({ isOpen: false });
          setActionModal({ isOpen: true, status: 'success', message: 'Sent to BC successfully!' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Sent to BC successfully', 'success', 'Done');
        } catch (error) {
          setActionModal({ isOpen: true, status: 'error', message: 'Error sending record: ' + error.message });
          // showToast('Error sending record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  const handlePostToBc = (issue) => {
    if (issue.PostSiteIndent) {
      setActionModal({ isOpen: true, status: 'error', message: 'This Issue has already been Posted to BC.' });
      return;
    }
    if (!issue.SendToBC) {
      setActionModal({ isOpen: true, status: 'error', message: 'Please Send to BC before Posting.' });
      return;
    }
    setConfirmModal({
      isOpen: true, title: 'Post to BC', message: `Post Site Indent ${issue.SiteIndentNo} to BC?`, isDanger: false,
      onConfirm: async () => {
        setActionModal({ isOpen: true, status: 'loading', message: 'Posting to Business Central...' });
        try {
          const token = localStorage.getItem('authToken');
          const payload = {
            documentNo: issue.IssueHeaderID,
            Issue: true,
            Receive: "False"
          };
          const response = await fetch(`${API_URL}/mapping/material-issue/post-to-bc`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
           const data = await response.json(); // 👈 ALWAYS parse

        if (!response.ok) {
          throw new Error(
            data?.error?.message ||
            data?.message ||
            JSON.stringify(data)
          );
        }
          fetchMaterialIssues();
          setActionModal({ isOpen: false }); 
          setActionModal({ isOpen: true, status: 'success', message: 'Posted to BC successfully!' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Posted to BC successfully', 'success', 'Done');
        } catch (error) {
          setActionModal({ isOpen: true, status: 'error', message: 'Error posting record: ' + error.message });
          // showToast('Error posting record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  // Add this handler function
const handleStatusFilterChange = (filterValue) => {
  setSelectedStatusFilters(prev => 
    prev.includes(filterValue) 
      ? prev.filter(item => item !== filterValue) 
      : [...prev, filterValue]
  );
};


  // --- NEW APPROVAL HANDLERS ---
  const openApprovalModal = (issueHeaderID, level) => {
    setApprovalModal({
      isOpen: true,
      issueHeaderID: issueHeaderID,
      currentLevel: level,
      remarks: ''
    });
  };

  const showToast = (message, type = 'success', title = 'Done', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  const handleApprovalAction = async (action) => {
    const { issueHeaderID, remarks } = approvalModal;

    if (action === 'REJECTED' && !remarks.trim()) {
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when rejecting.', 'error', 'Error');
      return;
    }
    if (action === 'APPROVED' && !remarks.trim()) {
      // alert('Remarks are mandatory when approving.');
      showToast('Remarks are mandatory when approving.', 'error', 'Error');
      return;
    }

    setApprovalModal({ ...approvalModal, isOpen: false });
    setActionModal({ isOpen: true, status: 'loading', message: `${action === 'APPROVED' ? 'Approving' : 'Rejecting'} Material Issue...` });

    try {
      const token = localStorage.getItem('authToken');
      // Assuming endpoints follow the /material-issue pattern. Verify with your backend.
      const endpoint = action === 'APPROVED' 
        ? `${API_URL}/material-issue/approve` 
        : `${API_URL}/material-issue/reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Using issueHeaderID to match Material Issue naming convention
        body: JSON.stringify({ issueHeaderID, remarks })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Action failed');
      }
      
      fetchMaterialIssues();
      setActionModal({ isOpen: false });
      // setActionModal({ isOpen: true, status: 'success', message: `Material Issue ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully` });
      // setTimeout(() => setActionModal({ isOpen: false }), 2000);
      showToast(
      `Material Issue ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully`, 
      action === 'APPROVED' ? 'success' : 'warning', 
      action === 'APPROVED' ? 'Done' : 'Action Completed'
);
    } catch (error) {
      // console.error(`Error:`, error);
      // setActionModal({ isOpen: true, status: 'error', message: `Error: ${error.message}` });
      showToast(`Error: ${error.message}`, 'error', 'Error');
    }
  };

  // Handler to trigger success message
  const handleFormSuccess = (result, actionType) => {
    setTimeout(() => {
      setSuccessModal({
        isOpen: true,
        id: result.IssueHeaderID,
        issueNo: result.IssueHeaderNo,
        action: actionType // 'Created' or 'Updated'
      });
    }, 300); 
  };

  // Handler to close the success message
  const handleCloseSuccess = () => {
    setSuccessModal({ isOpen: false, id: null, issueNo: '', action: '' });
    fetchMaterialIssues(); // Refresh list
  };

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (successModal.isOpen) {
      const timer = setTimeout(() => {
        handleCloseSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen]);

  // Filtering Logic
   // Filtering Logic
  const filteredIssues = issues.filter(issue => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = [
  issue.IssueHeaderNo,
  issue.ProjectNo,
  issue.ProjectDescription,
  issue.SiteIndentNo,
  issue.VendorCode,
  issue.VendorName,
  issue.ToFeederCode,
  issue.ToFeederDescription
].some(field => field?.toString().toLowerCase().includes(term));
    
    const matchesStatus = statusFilter === 'All' || issue.Status === statusFilter;
    
    // --- ADDED: Process Status Filter Logic (Including Post Site Indent) ---
    const matchesBcStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.some(filter => {
      
      // 1. Approval Phase
      if (filter === 'Pending') return issue.ApprovalStatus === 'PENDING';
      if (filter === 'Approved') return issue.ApprovalStatus === 'APPROVED';
      if (filter === 'Rejected') return issue.ApprovalStatus === 'REJECTED';

      // 2. BC Integration Phase
      // Ready to Send to BC (Approved but not sent)
      if (filter === 'Send to BC')
        return (
          !issue.SendToBC &&
          (issue.ApprovalStatus === 'APPROVED' || issue.ApprovalStatus == null)
        );

      // 3. The NEW Status: Post Site Indent (Sent, but not yet Posted)
      if (filter === 'Post Site Indent')
        return (
          issue.SendToBC === true && 
          !issue.PostSiteIndent
        );

      // 4. Final Success State
      if (filter === 'Posted Successfully') return issue.PostSiteIndent === true;

      return false;
    });
    // --------------------------------------------------------------

    return matchesSearch && matchesStatus && matchesBcStatus;
  });

  // Sorting Logic
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let valA = a[sortConfig.key] || '';
      let valB = b[sortConfig.key] || '';
      if (sortConfig.key === 'IssueDate' || sortConfig.key === 'CreatedAt') {
        valA = new Date(valA); valB = new Date(valB);
      } else {
        valA = valA.toString().toLowerCase(); valB = valB.toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedIssues = getSortedData(filteredIssues);
  const totalPages = Math.ceil(sortedIssues.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedIssues.slice(startIndex, startIndex + itemsPerPage);

  const renderSortArrows = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-xs ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="text-xs ml-1" /> : <FaSortDown className="text-xs ml-1" />;
  };

  const canCreate = hasFullAccess || hasPermission('MATERIAL_ISSUE', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MATERIAL_ISSUE', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MATERIAL_ISSUE', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MATERIAL_ISSUE', 'CanDelete');

  // 🔒 BUSINESS LOCK RULES (same as Site Indent)
// const isEditLocked = (issue) =>
//   issue.CurrentLevel >= 2 || (issue.CurrentLevel === 1 &&
//     ['APPROVED', 'REJECTED'].includes(issue.ApprovalStatus))
//    || issue.SendToBC === true ||
//   issue.PostSiteIndent === true;

  // 🔒 MATERIAL ISSUE LOCK RULES
  
  // --- EDIT LOGIC: Updated ---
  const isEditLocked = (issue) => {
    // 1. If Sent to BC or Posted, always Locked
    if ( issue.PostSiteIndent === true) return true;

    // 2. If Rejected, always Locked
    if (issue.ApprovalStatus === 'REJECTED') return true;
    
    if (!issue.ApprovalFlowID && !issue.SendToBC) {
      return false;
    }

    // 3. If Fully Approved (All levels done), Locked for EVERYONE
    // if (issue.ApprovalStatus === 'APPROVED') return true;

    // 4. If Pending Approval
    if (issue.ApprovalStatus === 'PENDING' && issue.CurrentLevel > 1) {
      // 'CanCurrentUserApprove' is calculated in SQL.
      // It is TRUE only if the logged-in user matches the CurrentApprover of the active level.
      if (issue.CanCurrentUserApprove ) {
        return false; // Unlocked for this specific approver
      }
      return true; // Locked for Entry User, Previous Approvers, and everyone else
    }

    // 5. Default (Draft/No Flow) - Unlocked
    return false;
  };
const downloadPDF = async (issueHeaderID) => {
  try {

    const response = await axios.get(
      `${API_URL}/designs/Material Issue/issuepdf/${issueHeaderID}`,
      {
        responseType: "arraybuffer"
      }
    );

    const blob = new Blob([response.data], { type: "application/pdf" });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `MaterialIssue_${issueHeaderID}.pdf`;
    link.click();

    window.URL.revokeObjectURL(url);

  } catch (error) {
    showToast(`Failed to download PDF: ${error.message}`, "error", "Error");
  }
};  

  const isDeleteLocked = (issue) => {
    const level = issue.CurrentLevel ?? 0;
    // "Same case with delete"
    
    // 1. If Rejected, always locked
    // if (item.ApprovalStatus === 'REJECTED') return true;

    // 2. If Current Level > 1, locked
    if (issue.CurrentLevel > 1) return true;

    if (issue.ApprovalStatus === 'APPROVED' && issue.DocumentApprovalID) {
      return true;
    }

    // 3. If Level 1 is Approved, locked
    if (issue.CurrentLevel === 1 && issue.ApprovalStatus === 'APPROVED') return true;

    // 4. Specific Logic: "Approved but no DocumentApprovalID"
    if (issue.ApprovalStatus === 'APPROVED' && !issue.DocumentApprovalID) {
      return issue.SendToBC === true || issue.PostSiteIndent === true;
    }

    // 5. Standard Lock: If Sent to BC or Validated
    if (issue.SendToBC === true || issue.PostSiteIndent === true) return true;

    return false;
  };



  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex materialissue-root">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }} onLogout={() => navigate('/login')} />
      
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Material Issue Management</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Create and manage Material Issues based on Site Indents.</p>
            </div>
            <div className="flex gap-2">

   {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm" style={{ backgroundColor: COLORS.BABY1, color: '#fff' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER} onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}>
                <FaPlus className="w-4 h-4" /> Create Issue
              </button>
            )}

            {/* PDF DESIGN BUTTON */}
            <button
              onClick={() => setIsPDFDesignerOpen(true)}
              className="flex items-center gap-2 px-4 py-2 font-medium rounded-md border shadow-sm transition-all duration-200 bg-white hover:bg-gray-50"
              style={{ borderColor: COLORS.BORDER, color: COLORS.TEXT }}
            >
              <FaEdit className="h-4 w-4" />

              <span>Design PDF</span>
            </button>

            </div>
        
          </div>
        </div>

        {/* Filters */}
               {/* Filters */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <input type="text" placeholder="Issue No, Project, Indent No..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full px-3 py-2 bg-white border focus:outline-none" style={{ borderColor: COLORS.BORDER }} />
            </div>
            
            
            {/* --- ADD THIS BLOCK HERE --- */}
            <div className="">
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.MUTED }}>Process Status</label>
              <div className="flex flex-wrap gap-4 text-sm px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
                {['Pending', 'Approved', 'Rejected', 'Send to BC', 'Post Site Indent', 'Posted Successfully'].map((label) => (
                  <label key={label} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      checked={selectedStatusFilters.includes(label)}
                      onChange={() => handleStatusFilterChange(label)}
                    />
                    <span style={{ color: COLORS.TEXT }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* --------------------------- */}

            {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
                <option value="All">All Status</option>
                <option value="Open">Open</option>
                <option value="Released">Released</option>
                <option value="Closed">Closed</option>
              </select>
            </div> */}

          </div>
        </div>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Material Issue List</h2>
            <div className="flex items-center space-x-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2" style={{color: currentPage === 1 ? '#ccc' : COLORS.TEXT}}><FaChevronLeft /></button>
              <span style={{color: COLORS.TEXT}}>{currentPage} / {totalPages || 1}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-2" style={{color: currentPage === totalPages || totalPages === 0 ? '#ccc' : COLORS.TEXT}}><FaChevronRight /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : currentItems.length === 0 ? <div className="text-center py-8 text-gray-500">No records found</div> : (
              <table className="w-full">
                <thead className="text-xs font-semibold text-left uppercase" style={{ backgroundColor: COLORS.BABY, color: COLORS.TEXT, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'IssueHeaderNo', direction: sortConfig.key === 'IssueHeaderNo' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>Issue No {renderSortArrows('IssueHeaderNo')}</th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'IssueDate', direction: sortConfig.key === 'IssueDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>Date {renderSortArrows('IssueDate')}</th>
                    <th className="px-4 py-3">Site Indent Ref</th>
                    <th className="px-4 py-3">Project</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">To Feeder</th>

                    {/* <th className="px-4 py-3">Status</th> */}
                    <th className="px-4 py-3">Approval/BC Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((issue, idx) => (
                    <tr key={issue.IssueHeaderID || idx} className="hover:bg-[#faf9f8] transition-colors">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.TEXT }}>{issue.IssueHeaderNo}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{formatDate(issue.IssueDate)}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{issue.SiteIndentNo || '-'}</td>
                     <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{issue.ProjectNo}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{issue.ProjectDescription || '-'}</div>
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{issue.VendorCode || '-'}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{issue.VendorName || '-'}</div>
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{issue.ToFeederCode}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{issue.ToFeederDescription || '-'}</div>
                      </td>
                      {/* <td className="px-4 py-3">
                        <span className={`status-badge ${issue.Status === 'Open' ? 'bg-green-100 text-green-800' : issue.Status === 'Released' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {issue.Status || 'Draft'}
                        </span>
                      </td> */}
                      {/* UPDATED LOGIC MIRRORING SITE INDENT */}
                      <td className='px-4 py-3'>
                        {issue.ApprovalStatus === 'PENDING' ? (
                          // --- APPROVAL PHASE ---
                          issue.CanCurrentUserApprove ? (
                            <button 
                              onClick={() => openApprovalModal(issue.IssueHeaderID, issue.CurrentLevel)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
                              style={{ backgroundColor: '#16A34A' }} // Green
                            >
                              Approve (Lvl {issue.CurrentLevel})
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500 italic flex items-center gap-1">
                              Approval is going on (Lvl {issue.CurrentLevel})
                            </span>
                          )

                        ) : issue.ApprovalStatus === 'REJECTED' ? (
                          // --- REJECTED STATE ---
                          <span className="text-xs font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-200">
                            Rejected
                          </span>

                        ) : (
                          // --- POST-APPROVAL PHASE ---
                          <>
                            {issue.PostSiteIndent ? (
                              <span className="text-xs font-semibold text-green-600 flex items-center gap-1 bg-purple-50 px-2 py-1 rounded border border-green-200">Material issued successfully</span>
                            ) : issue.SendToBC ? (
                              <button onClick={() => handlePostToBc(issue)} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 flex items-center gap-1"><FaCloudUploadAlt className="text-xs" /> Post to BC</button>
                            ) : (
                              <button onClick={() => handleSendToBc(issue)} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">Send to BC</button>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                     <div className="flex justify-center items-center space-x-3" style={{ color: COLORS.MUTED }}>

    {canView && (<button onClick={() => handleView(issue)} title="View" className="hover:text-black"><FaEye /></button>)}
                          {canEdit && (
  <button
    onClick={() => !isEditLocked(issue) && handleEdit(issue)}
    disabled={isEditLocked(issue)}
    title={
      isEditLocked(issue)
        ? 'Edit disabled after approval starts'
        : 'Edit'
    }
    className={`${
      isEditLocked(issue)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-blue-600'
    }`}
  >
    <FaEdit />
  </button>
)}

                         {canDelete && (
  <button
    onClick={() =>
      !isDeleteLocked(issue) &&
      handleDelete(issue.IssueHeaderID)
    }
    disabled={isDeleteLocked(issue)}
    title={
      isDeleteLocked(issue)
        ? 'Delete disabled after approval / BC posting'
        : 'Delete'
    }
    className={`${
      isDeleteLocked(issue)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-red-600'
    }`}
  >
    <FaTrash />
  </button>
)}

    <button
    onClick={() => issue.PostSiteIndent && downloadPDF(issue.IssueHeaderID)}
    disabled={!issue.PostSiteIndent}
    title={
      !issue.PostSiteIndent
        ? 'PDF download enabled only after posting to BC'
        : 'Download PDF'
    }
    className={`${
      !issue.PostSiteIndent
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-green-600'
    }`}
  >
    <FaDownload />
  </button>

</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>

      {/* MODALS */}
      {showFormModal && (
        <MaterialIssueFormModal
          open={showFormModal}
          onClose={() => setShowFormModal(false)}
          initialData={selectedIssue}
          onSubmit={fetchMaterialIssues}
           onFormSuccess={handleFormSuccess} //
        />
      )}

      {showViewModal && selectedIssue && (
        <MaterialIssueViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          data={selectedIssue}
        />
      )}

      {/* --- APPROVAL/REJECT MODAL (COPIED FROM SITE INDENT) --- */}
      {approvalModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50">
          <div className="relative bg-white shadow-xl max-w-md w-full mx-4 p-6 rounded-lg animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Review Material Issue: {approvalModal.issueHeaderID} (Level {approvalModal.currentLevel})
            </h3>
            
            {/* Remarks Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks {<span className="text-red-500">*</span>}
              </label>
              <textarea
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter remarks..."
                value={approvalModal.remarks}
                onChange={(e) => setApprovalModal({ ...approvalModal, remarks: e.target.value })}
              ></textarea>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprovalAction('REJECTED')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprovalAction('APPROVED')}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* --- SUCCESS MODAL --- */}
      {successModal.isOpen && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-slideDown">
          <div className="flex items-center gap-4 bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 min-w-[420px]">
            {/* Icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <SuccessIcon size={22} />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800">
                Material Issue {successModal.action} Successfully
              </h4>
              <p className="text-xs text-gray-500">
                Issue No: <span className="font-medium">{successModal.issueNo}</span>
              </p>
            </div>

            {/* Loader bar */}
            <div className="absolute bottom-0 left-0 h-[3px] bg-green-500 rounded-b-xl animate-progress" />
          </div>

          <style>
            {`
              @keyframes slideDown {
                from { opacity: 0; transform: translate(-50%, -10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
              @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
              }
              .animate-slideDown { animation: slideDown 0.25s ease-out; }
              .animate-progress { animation: progress 2s linear; }
                
            `}
          </style>
        </div>
      )}

            {/* --- TOAST NOTIFICATION --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`
            relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
            ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
            ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
            ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
          `}>
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
          <style>
            {`
             
                /* Add this to the end of your existing style.innerHTML string */
                @keyframes toastIn {
                  from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
                  to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes toastProgress {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .animate-toastIn { animation: toastIn 0.25s ease-out; }
                .animate-toastProgress { animation: toastProgress 4s linear forwards; }

            `}
          </style>
        </div>
      )}
{isPDFDesignerOpen && (
  <PDFDesigner
    isOpen={isPDFDesignerOpen}
    onClose={() => setIsPDFDesignerOpen(false)}
    entity="Material Issue"

    sampleData={{
      IssueHeaderNo: "MI-0001",
      IssueDate: "2026-03-01",
      SiteIndentNo: "SI-0001",
      SurveyBOQNo: "BOQ-0001",
      VendorCode: "VENDOR001",
      VendorName:"AATRI",
      VendorAddress:"B-3/78 NEW DELHI",

      ProjectNo: "PRJ-0001",

      FromFeederCode: "F001",
      FromFeederDescription: "Main Feeder",
      FromFeederAddress1:"Sector 2 ",
  
      FromFeederGst: "29AABCT1234A1ZM",

      ToFeederCode: "F002",
      ToFeederDescription: "Secondary Feeder",
      ToFeederAddress1 : "Sector 12 Phase 3",
      ToFeederGst: "29AABCT5678B1ZM",

      Circle: "MOHAN",
      District: "GHAZIABAD",
      Division: "CITY TRANSMISSION DI",
      SubDivision: "SUB DIVISION-II",
      SubStation: "132 KV NARAYANGARH",

      FeederEngineerName: "Rahul Sharma",

      ApprovalStatus: "APPROVED",
      CurrentLevel: 1,
      SendToBC: false,
      PostSiteIndent: false,

      Remarks: "Material issued successfully",

      Items: [
        {
          IssueLineNumber: 100,
          ProductCode: "ITM000001",
          Description: "08MM ESL TMT BAR",
          TaskNo: "01",
          UnitOfMeasure: "KGS",
          SurveyQty: 500,
          IndentQty: 400,
          QtyToIssue: 350
        }
      ],

      Summary: {
        totalLines: 1,
        totalQtyToIssue: 350
      }
    }}

    availableFields={{
      Header: [
        "IssueHeaderNo",
        "IssueDate",
        "SiteIndentNo",
        "SurveyBOQNo",
        "VendorCode",
        "VendorName",
        "VendorAddress",
        "ProjectNo",

        "FromFeederCode",
        "FromFeederDescription",
        "FromFeederAddress1",
         // --- ADD THESE 2 LINES ---
       
        "FromFeederGst",

        "ToFeederCode",
        "ToFeederDescription",
        "ToFeederAddress1",
       
        "ToFeederGst",

        "Circle",
        "District",
        "Division",
        "SubDivision",
        "SubStation",

        "FeederEngineerName",

        "ApprovalStatus",
       

        "Remarks"
      ],

      Items: [
        "IssueLineNumber",
        "ProductCode",
        "Description",
        "TaskNo",
        "UnitOfMeasure",
        "SurveyQty",
        "IndentQty",
        "QtyToIssue"
      ],

      Summary: [
        "totalLines",
        "totalQtyToIssue"
      ]
    }}
  />
)}
      <ActionModal isOpen={actionModal.isOpen} status={actionModal.status} message={actionModal.message} onClose={() => setActionModal({ ...actionModal, isOpen: false })} />
      <ConfirmationDialog isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} onConfirm={() => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); }} onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} isDanger={confirmModal.isDanger} />
    </div>
  );
};

export default MaterialIssueItems;